/**
 * GraphQL Critique Mutation Hooks
 *
 * Hooks for critique operations: create, update, delete critiques,
 * critique edits, and critique sessions.
 */

import { useMutation, gql } from '@apollo/client';
import { GET_CRITIQUE_SESSION } from '../../graphql/queries';
import logger from '../../utils/logger';

// Critique Mutations
const CREATE_CRITIQUE_MUTATION = gql`
  mutation CreateCritique($input: CreateCritiqueInput!) {
    createCritique(input: $input)
  }
`;

const UPDATE_CRITIQUE_MUTATION = gql`
  mutation UpdateCritique($input: UpdateCritiqueInput!) {
    updateCritique(input: $input)
  }
`;

const DELETE_CRITIQUE_MUTATION = gql`
  mutation DeleteCritique($input: DeleteCritiqueInput!) {
    deleteCritique(input: $input) {
      success
      message
    }
  }
`;

// Critique Edit Mutations
const CREATE_CRITIQUE_EDIT_MUTATION = gql`
  mutation CreateCritiqueEdit($input: CreateCritiqueEditInput!) {
    createCritiqueEdit(input: $input)
  }
`;

const UPDATE_CRITIQUE_EDIT_MUTATION = gql`
  mutation UpdateCritiqueEdit($input: UpdateCritiqueEditInput!) {
    updateCritiqueEdit(input: $input)
  }
`;

const DELETE_CRITIQUE_EDIT_MUTATION = gql`
  mutation DeleteCritiqueEdit($input: DeleteCritiqueEditInput!) {
    deleteCritiqueEdit(input: $input) {
      success
      message
    }
  }
`;

// Critique Session Mutations
const UPDATE_CRITIQUE_SESSION_MUTATION = gql`
  mutation UpdateCritiqueSession($input: UpdateCritiqueSessionInput!) {
    updateCritiqueSession(input: $input)
  }
`;

const DELETE_CRITIQUE_SESSION_MUTATION = gql`
  mutation DeleteCritiqueSession($input: DeleteCritiqueSessionInput!) {
    deleteCritiqueSession(input: $input) {
      success
      message
    }
  }
`;


// Types
interface SuccessPayload {
  success: boolean;
  message?: string;
}

// Input types - Critiques
interface CreateCritiqueInput {
  photoId: string;
  content: string;
  collectionId?: string;
  sessionId?: string;
  parentId?: string;
  editId?: string;
}

interface UpdateCritiqueInput {
  id: string;
  content: string;
}

interface DeleteCritiqueInput {
  id: string;
}

// Input types - Critique Edits
interface CreateCritiqueEditInput {
  photoId: string;
  editType: string;
  editData: Record<string, unknown>;
  baseEditId?: string;
}

interface UpdateCritiqueEditInput {
  photoId: string;
  editId: string;
  editData: Record<string, unknown>;
}

interface DeleteCritiqueEditInput {
  photoId: string;
  editId: string;
}

// Input types - Critique Sessions
interface NCommentsConfigInput {
  targetCount: number;
}

interface UpdateCritiqueSessionInput {
  id: string;
  title?: string;
  description?: string | null;
  state?: string;
  feedbackOptions?: string[];  // Feature 649: Enabled feedback types
  nCommentsConfig?: NCommentsConfigInput;  // Feature 651: N Comments config
}

interface DeleteCritiqueSessionInput {
  id: string;
}


// ===== Critique Hooks =====

/**
 * Hook for creating a critique
 */
export function useCreateCritiqueGraphQL() {
  const [mutate, { data, loading, error }] = useMutation<
    { createCritique: unknown },
    { input: CreateCritiqueInput }
  >(CREATE_CRITIQUE_MUTATION);

  const createCritique = async (input: CreateCritiqueInput) => {
    logger.info('Critiques', 'Creating critique via GraphQL', { photoId: input.photoId });

    const result = await mutate({ variables: { input } });

    return result.data?.createCritique;
  };

  return {
    createCritique,
    data: data?.createCritique,
    loading,
    error,
  };
}

/**
 * Hook for updating a critique
 */
export function useUpdateCritiqueGraphQL() {
  const [mutate, { data, loading, error }] = useMutation<
    { updateCritique: unknown },
    { input: UpdateCritiqueInput }
  >(UPDATE_CRITIQUE_MUTATION);

  const updateCritique = async (input: UpdateCritiqueInput) => {
    logger.info('Critiques', 'Updating critique via GraphQL', { id: input.id });

    const result = await mutate({ variables: { input } });

    return result.data?.updateCritique;
  };

  return {
    updateCritique,
    data: data?.updateCritique,
    loading,
    error,
  };
}

/**
 * Hook for deleting a critique
 */
export function useDeleteCritiqueGraphQL() {
  const [mutate, { data, loading, error }] = useMutation<
    { deleteCritique: SuccessPayload },
    { input: DeleteCritiqueInput }
  >(DELETE_CRITIQUE_MUTATION);

  const deleteCritique = async (id: string) => {
    logger.info('Critiques', 'Deleting critique via GraphQL', { id });

    const result = await mutate({ variables: { input: { id } } });

    return result.data?.deleteCritique;
  };

  return {
    deleteCritique,
    data: data?.deleteCritique,
    loading,
    error,
  };
}

// ===== Critique Edit Hooks =====

/**
 * Hook for creating a critique edit
 */
export function useCreateCritiqueEditGraphQL() {
  const [mutate, { data, loading, error }] = useMutation<
    { createCritiqueEdit: unknown },
    { input: CreateCritiqueEditInput }
  >(CREATE_CRITIQUE_EDIT_MUTATION);

  const createCritiqueEdit = async (input: CreateCritiqueEditInput) => {
    logger.info('CritiqueEdits', 'Creating critique edit via GraphQL', {
      photoId: input.photoId,
      editType: input.editType,
    });

    const result = await mutate({ variables: { input } });

    return result.data?.createCritiqueEdit;
  };

  return {
    createCritiqueEdit,
    data: data?.createCritiqueEdit,
    loading,
    error,
  };
}

/**
 * Hook for updating a critique edit
 */
export function useUpdateCritiqueEditGraphQL() {
  const [mutate, { data, loading, error }] = useMutation<
    { updateCritiqueEdit: unknown },
    { input: UpdateCritiqueEditInput }
  >(UPDATE_CRITIQUE_EDIT_MUTATION);

  const updateCritiqueEdit = async (input: UpdateCritiqueEditInput) => {
    logger.info('CritiqueEdits', 'Updating critique edit via GraphQL', {
      photoId: input.photoId,
      editId: input.editId,
    });

    const result = await mutate({ variables: { input } });

    return result.data?.updateCritiqueEdit;
  };

  return {
    updateCritiqueEdit,
    data: data?.updateCritiqueEdit,
    loading,
    error,
  };
}

/**
 * Hook for deleting a critique edit
 */
export function useDeleteCritiqueEditGraphQL() {
  const [mutate, { data, loading, error }] = useMutation<
    { deleteCritiqueEdit: SuccessPayload },
    { input: DeleteCritiqueEditInput }
  >(DELETE_CRITIQUE_EDIT_MUTATION);

  const deleteCritiqueEdit = async (photoId: string, editId: string) => {
    logger.info('CritiqueEdits', 'Deleting critique edit via GraphQL', { photoId, editId });

    const result = await mutate({ variables: { input: { photoId, editId } } });

    return result.data?.deleteCritiqueEdit;
  };

  return {
    deleteCritiqueEdit,
    data: data?.deleteCritiqueEdit,
    loading,
    error,
  };
}

// ===== Critique Session Hooks =====

/**
 * Hook for updating a critique session
 */
export function useUpdateCritiqueSessionGraphQL() {
  const [mutate, { data, loading, error }] = useMutation<
    { updateCritiqueSession: unknown },
    { input: UpdateCritiqueSessionInput }
  >(UPDATE_CRITIQUE_SESSION_MUTATION);

  const updateCritiqueSession = async (input: UpdateCritiqueSessionInput) => {
    logger.info('CritiqueSessions', 'Updating critique session via GraphQL', { id: input.id });

    const result = await mutate({
      variables: { input },
      refetchQueries: [
        { query: GET_CRITIQUE_SESSION, variables: { id: input.id } }
      ],
    });

    return result.data?.updateCritiqueSession;
  };

  return {
    updateCritiqueSession,
    data: data?.updateCritiqueSession,
    loading,
    error,
  };
}

/**
 * Hook for deleting a critique session
 */
export function useDeleteCritiqueSessionGraphQL() {
  const [mutate, { data, loading, error }] = useMutation<
    { deleteCritiqueSession: SuccessPayload },
    { input: DeleteCritiqueSessionInput }
  >(DELETE_CRITIQUE_SESSION_MUTATION);

  const deleteCritiqueSession = async (id: string) => {
    logger.info('CritiqueSessions', 'Deleting critique session via GraphQL', { id });

    const result = await mutate({ variables: { input: { id } } });

    return result.data?.deleteCritiqueSession;
  };

  return {
    deleteCritiqueSession,
    data: data?.deleteCritiqueSession,
    loading,
    error,
  };
}


/**
 * Combined hook for all critique mutations
 */
export function useCritiqueMutationsGraphQL() {
  return {
    // Critiques
    useCreateCritique: useCreateCritiqueGraphQL,
    useUpdateCritique: useUpdateCritiqueGraphQL,
    useDeleteCritique: useDeleteCritiqueGraphQL,
    // Critique Edits
    useCreateCritiqueEdit: useCreateCritiqueEditGraphQL,
    useUpdateCritiqueEdit: useUpdateCritiqueEditGraphQL,
    useDeleteCritiqueEdit: useDeleteCritiqueEditGraphQL,
    // Critique Sessions
    useUpdateCritiqueSession: useUpdateCritiqueSessionGraphQL,
    useDeleteCritiqueSession: useDeleteCritiqueSessionGraphQL,
  };
}
